/**
 * tripInsert.ts — where a newly added place goes in the trip.
 *
 * Road trips are mostly built in order (Melbourne, then Halls Gap, then Mount
 * Gambier), so a place beyond the destination EXTENDS the trip and the old
 * destination becomes a 1-night overnight; a place genuinely between two
 * stops goes into the gap that adds the least driving (placeNewStop).
 */

import type { TripLeg, TripStop } from '../types/trip';
import { haversineDistanceKm, placeNewStop } from './tripPlanner';

/** ~500 m: two picks of "Melbourne" from different sources land this close. */
const SAME_PLACE_DEG = 0.005;

function samePlace(a: TripStop, b: TripStop): boolean {
  if (typeof a.entityId === 'number' && typeof b.entityId === 'number') {
    return a.entityId === b.entityId;
  }
  if (
    typeof a.lat === 'number' && typeof a.lng === 'number' &&
    typeof b.lat === 'number' && typeof b.lng === 'number'
  ) {
    return Math.abs(a.lat - b.lat) < SAME_PLACE_DEG && Math.abs(a.lng - b.lng) < SAME_PLACE_DEG;
  }
  return a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
}

/** True when the trip finishes where it starts (a "Back home" loop). */
export function isLoopTrip(stops: TripStop[]): boolean {
  if (stops.length < 2) return false;
  return samePlace(stops[0], stops[stops.length - 1]);
}

/** Cheapest gap (1..n-1) to slot a point into — the loop rule: never past the finish. */
function cheapestGap(stops: TripStop[], p: { lat: number; lng: number }): number {
  let best = stops.length - 1;
  let bestCost = Infinity;
  for (let i = 1; i < stops.length; i += 1) {
    const a = stops[i - 1];
    const b = stops[i];
    if (
      typeof a.lat !== 'number' || typeof a.lng !== 'number' ||
      typeof b.lat !== 'number' || typeof b.lng !== 'number'
    ) {
      continue;
    }
    const cost =
      haversineDistanceKm(a.lat, a.lng, p.lat, p.lng) +
      haversineDistanceKm(p.lat, p.lng, b.lat, b.lng) -
      haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
    if (cost < bestCost) {
      bestCost = cost;
      best = i;
    }
  }
  return best;
}

export const mergeTripStopIntoList = (
  stops: TripStop[],
  stop: TripStop
): { added: boolean; next: TripStop[]; asNewEnd?: boolean; index?: number } => {
  const loop = isLoopTrip(stops);
  // In a loop the finish is the start again — only the start counts as taken.
  const pool = loop ? stops.slice(0, -1) : stops;
  const alreadyExists = pool.some((existing) => {
    if (existing.entityId && stop.entityId) {
      return existing.entityId === stop.entityId;
    }
    return existing.name.trim().toLowerCase() === stop.name.trim().toLowerCase();
  });

  if (alreadyExists) {
    return { added: false, next: stops };
  }

  if (loop) {
    // A round trip finishes back home: a new place goes in the gap that adds
    // the least driving, never past the finish.
    const index =
      typeof stop.lat === 'number' && typeof stop.lng === 'number'
        ? cheapestGap(stops, { lat: stop.lat, lng: stop.lng })
        : stops.length - 1;
    return { added: true, next: [...stops.slice(0, index), stop, ...stops.slice(index)], asNewEnd: false, index };
  }

  if (stops.length < 2) {
    return { added: true, next: [...stops, stop], asNewEnd: stops.length === 1, index: stops.length };
  }

  // Trips are mostly built in order, so a place beyond the destination
  // EXTENDS the trip (the old destination becomes a 1-night overnight); a
  // place genuinely between two stops goes into the gap that adds the least
  // driving. See placeNewStop for the rule and its numbers.
  const placement =
    typeof stop.lat === 'number' && typeof stop.lng === 'number'
      ? placeNewStop(stops, { lat: stop.lat, lng: stop.lng })
      : { index: stops.length, asNewEnd: true };
  if (placement.asNewEnd) {
    const oldEnd = stops[stops.length - 1];
    const promoted: TripStop = { ...oldEnd, nights: Math.max(1, oldEnd.nights ?? 0) };
    return {
      added: true,
      next: [...stops.slice(0, -1), promoted, stop],
      asNewEnd: true,
      index: stops.length,
    };
  }
  const index = placement.index;
  return { added: true, next: [...stops.slice(0, index), stop, ...stops.slice(index)], asNewEnd: false, index };
};

/** Move the stop at `index` to the end (it becomes the destination; the old
 *  destination becomes a 1-night overnight). */
export const makeStopTheDestination = (stops: TripStop[], stopId: string): TripStop[] => {
  const i = stops.findIndex((s) => s.id === stopId);
  if (i < 0 || i === stops.length - 1) return stops;
  const moving = stops[i];
  const rest = stops.filter((_, k) => k !== i);
  const oldEnd = rest[rest.length - 1];
  return [...rest.slice(0, -1), { ...oldEnd, nights: Math.max(1, oldEnd.nights ?? 0) }, moving];
};


/**
 * Put a stop at an exact position ("+" between two ladder rows). Same
 * duplicate rule as mergeTripStopIntoList; the index is clamped so it can
 * never land before the start or after the destination.
 */
export const insertStopAt = (
  stops: TripStop[],
  stop: TripStop,
  index: number
): { added: boolean; next: TripStop[]; index: number } => {
  const alreadyExists = stops.some((existing) =>
    existing.entityId && stop.entityId
      ? existing.entityId === stop.entityId
      : existing.name.trim().toLowerCase() === stop.name.trim().toLowerCase()
  );
  if (alreadyExists) return { added: false, next: stops, index: -1 };
  // Between two stops means 1..length-1; with fewer than two stops there's no
  // "between", so it appends.
  const at = stops.length < 2 ? stops.length : Math.max(1, Math.min(stops.length - 1, Math.round(index)));
  return { added: true, next: [...stops.slice(0, at), stop, ...stops.slice(at)], index: at };
};

/* ── What a pick would cost ─────────────────────────────────────────────── */

export interface PlacementInfo {
  /** Plain words for where it goes: "Between Melbourne and Sydney",
   *  "After Sydney, as the new end", "Next stop after Melbourne". */
  where: string;
  /** Extra road distance it adds to the trip (an estimate), km. */
  addKm: number;
  /** Extra driving it adds (an estimate), minutes; null without routed legs. */
  addMin: number | null;
  /** True when it adds next to nothing (a few km): the chip says "On route". */
  onRoute: boolean;
}

type PlacePoint = Pick<TripStop, 'id' | 'name' | 'lat' | 'lng'>;
type LegLike = Pick<TripLeg, 'fromId' | 'distanceKm' | 'driveMinutes'>;

/** Australian roads run about a quarter longer than the straight line. */
const DEFAULT_ROAD_FACTOR = 1.28;
/** Without a routed leg to learn from: about 80 km/h door to door. */
const DEFAULT_MIN_PER_KM = 0.75;
/** A detour leaves the highway: it drives slower than the leg's average. */
const DETOUR_SLOWDOWN = 1.25;
/** Under this many extra km, it's on the way. */
export const ON_ROUTE_KM = 5;

const hasPoint = (s: Partial<PlacePoint>): s is PlacePoint & { lat: number; lng: number } =>
  typeof s.lat === 'number' && typeof s.lng === 'number' && Number.isFinite(s.lat) && Number.isFinite(s.lng);

const kmBetween = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) =>
  haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);

/**
 * Where a place would land and roughly what it adds, so a search row can say
 * "+100 km" instead of "220 km off route". Distance off the CURRENT road line
 * punished a town on the obvious coast road; the honest figure is the
 * detour: (A→P + P→B − A→B), scaled by how winding the routed leg actually
 * is. `insertAt` pins the gap (the "+" between two stops); otherwise it goes
 * where the top box would put it (placeNewStop). Null before there's a trip.
 */
export const placementInfo = (
  stops: PlacePoint[],
  p: { lat: number; lng: number },
  legs: LegLike[] = [],
  insertAt?: number
): PlacementInfo | null => {
  if (!stops.length || !hasPoint(p as Partial<PlacePoint>)) return null;
  const legFrom = (id: string) => legs.find((l) => l.fromId === id);
  // Road factor and pace from a routed leg, when there is one.
  const learn = (a: PlacePoint, b: PlacePoint) => {
    const leg = legFrom(a.id);
    const straight = hasPoint(a) && hasPoint(b) ? kmBetween(a, b) : 0;
    const factor =
      leg && typeof leg.distanceKm === 'number' && straight > 20
        ? Math.min(1.7, Math.max(1.1, leg.distanceKm / straight))
        : DEFAULT_ROAD_FACTOR;
    const pace =
      leg && typeof leg.distanceKm === 'number' && typeof leg.driveMinutes === 'number' && leg.distanceKm > 0
        ? leg.driveMinutes / leg.distanceKm
        : null;
    return { factor, pace };
  };
  const minutes = (km: number, pace: number | null) =>
    pace === null ? null : Math.round(km * pace * DETOUR_SLOWDOWN);

  const last = stops[stops.length - 1];
  const toEnd = (): PlacementInfo | null => {
    if (!hasPoint(last)) return null;
    const prev = stops.length >= 2 ? stops[stops.length - 2] : null;
    const { factor, pace } = prev ? learn(prev, last) : { factor: DEFAULT_ROAD_FACTOR, pace: null };
    const addKm = kmBetween(last, p) * factor;
    return {
      where: stops.length === 1 ? `Next stop after ${last.name}` : `After ${last.name}, as the new end`,
      addKm,
      addMin: minutes(addKm, pace ?? (stops.length === 1 ? DEFAULT_MIN_PER_KM : null)),
      onRoute: false,
    };
  };
  if (stops.length === 1) return toEnd();

  let gap: number;
  if (typeof insertAt === 'number') {
    gap = Math.max(1, Math.min(stops.length - 1, Math.round(insertAt)));
  } else if (isLoopTrip(stops as TripStop[])) {
    // A round trip finishes back home: same rule as mergeTripStopIntoList.
    gap = cheapestGap(stops as TripStop[], p);
  } else {
    const at = placeNewStop(stops, p);
    if (at.asNewEnd) return toEnd();
    gap = at.index;
  }
  const a = stops[gap - 1];
  const b = stops[gap];
  if (!hasPoint(a) || !hasPoint(b)) return null;
  const { factor, pace } = learn(a, b);
  const extra = Math.max(0, kmBetween(a, p) + kmBetween(p, b) - kmBetween(a, b)) * factor;
  return {
    where: `Between ${a.name} and ${b.name}`,
    addKm: extra,
    addMin: minutes(extra, pace),
    onRoute: extra < ON_ROUTE_KM,
  };
};

/** "1h 20m" / "35 min", rounded to 5 minutes (it's an estimate). */
export const formatAddedDrive = (min: number): string => {
  const m = Math.max(5, Math.round(min / 5) * 5);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

/** "+100 km": the chip on a search row. Rounded so it doesn't pretend. */
export const formatAddedKm = (km: number): string => {
  const r = km < 20 ? Math.max(1, Math.round(km)) : km < 100 ? Math.round(km / 5) * 5 : Math.round(km / 10) * 10;
  return `+${r.toLocaleString('en-AU')} km`;
};

/** Within this of the drawn road, the out-and-back to reach it is the cost. */
const NEAR_ROAD_KM = 30;

/**
 * Square a straight-line detour with the drawn road. Near the road, reaching
 * a place means leaving it and coming back, so the cost is at least twice the
 * distance off it (a campground 11 km up a side road isn't "on route" just
 * because it sits on the straight line). Far from the road (a coast town off
 * an inland highway), inserting it re-routes the drive, and the detour
 * estimate stands. Within 2 km of the road it's on route.
 */
export const withRoadDistance = (info: PlacementInfo | null, offRoadKm: number | null | undefined): PlacementInfo | null => {
  if (!info || typeof offRoadKm !== 'number' || !Number.isFinite(offRoadKm)) return info;
  if (offRoadKm <= 2) return { ...info, onRoute: true, addKm: Math.min(info.addKm, ON_ROUTE_KM - 1) };
  if (offRoadKm > NEAR_ROAD_KM) return info;
  const addKm = Math.max(info.addKm, offRoadKm * 2);
  const scale = info.addKm > 0 ? addKm / info.addKm : 1;
  return {
    ...info,
    addKm,
    onRoute: false,
    addMin: info.addMin === null ? null : Math.round(info.addKm > 0 ? info.addMin * scale : addKm * 1.1),
  };
};

/**
 * A cheaper place for a stop that is already in the trip ("Move to best
 * spot" after an add). Only the gaps between stops count: the start and the
 * destination are the user's own. Straight-line detours, so it needs
 * coordinates on the stop and its neighbours. Returns null unless moving it
 * saves at least `minSavedKm` (default 15 km) AND a fifth of its current
 * detour, so a near-tie never nags.
 */
export const betterSpot = (
  stops: TripStop[],
  stopId: string,
  minSavedKm = 15
): { index: number; savedKm: number } | null => {
  const i = stops.findIndex((s) => s.id === stopId);
  if (i <= 0 || i >= stops.length - 1) return null;
  const s = stops[i];
  if (typeof s.lat !== 'number' || typeof s.lng !== 'number') return null;
  const p = { lat: s.lat, lng: s.lng };
  const rest = stops.filter((_, k) => k !== i);
  const gapCost = (k: number): number => {
    const a = rest[k - 1];
    const b = rest[k];
    if (
      typeof a.lat !== 'number' || typeof a.lng !== 'number' ||
      typeof b.lat !== 'number' || typeof b.lng !== 'number'
    ) {
      return Infinity;
    }
    return (
      haversineDistanceKm(a.lat, a.lng, p.lat, p.lng) +
      haversineDistanceKm(p.lat, p.lng, b.lat, b.lng) -
      haversineDistanceKm(a.lat, a.lng, b.lat, b.lng)
    );
  };
  const current = gapCost(i);
  if (!Number.isFinite(current)) return null;
  let best = i;
  let bestCost = current;
  for (let k = 1; k < rest.length; k += 1) {
    const c = gapCost(k);
    if (c < bestCost) {
      bestCost = c;
      best = k;
    }
  }
  const saved = current - bestCost;
  if (best === i || saved < minSavedKm || saved < current * 0.2) return null;
  return { index: best, savedKm: Math.round(saved) };
};

/** The trip with the stop moved to `index` (an index into the list without it). */
export const moveStopToGap = (stops: TripStop[], stopId: string, index: number): TripStop[] => {
  const i = stops.findIndex((s) => s.id === stopId);
  if (i < 0) return stops;
  const rest = stops.filter((_, k) => k !== i);
  const at = Math.max(1, Math.min(rest.length - 1, index));
  return [...rest.slice(0, at), stops[i], ...rest.slice(at)];
};
